// EditPlant.js
import React, { useState, useEffect } from 'react';
import "../../Assets/Styles/Form.css";
import AxiosInstance from '../Axios/AxiosInstance';
import * as FaIcons from "react-icons/fa";
import { convertBoundaryToJson, convertJsonToBoundaryCSV } from './BoundaryUtils';

// convertJsonToBoundaryCSV imported from BoundaryUtils

const emptyForm = {
    company_id: '',
    plant_name: '',
    contact_person_name: '',
    contact_number: '',
    contact_email_id: '',
    plant_address: '',
    plant_lat: '',
    plant_lon: '',
    plant_area: '',
    plant_boundry: '',      // shown in textarea as CSV
    plant_time_zone_diff: '',
    critical_battery_level: '',
    plant_added_date: '',
    company_name: '',
    plant_id: '',
    plant_mode: 'Update',
    message: '',
};

export const EditPlant = () => {
    const _userID = sessionStorage.getItem("user_id");
    const _plantID = sessionStorage.getItem("editpid");   // set by SearchPlant on row click

    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [boundaryPreview, setBoundaryPreview] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // -----------------------------------------------------------------------
    // On mount: load the plant record identified by sessionStorage editpid
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!_plantID) {
            setLoadError("No farm selected. Please go back to Search Farm and click a Farm ID.");
            setIsLoading(false);
            return;
        }
        fetchPlant();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPlant = async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await AxiosInstance.get(
                `/Plant/GetPlant?pid=${_plantID}&uid=${_userID}&pn=EditPlant&sn=GetPlantByPlantId`
            );

            const raw = Array.isArray(response.data)
                ? response.data
                : response.data?.$values ?? [];   // handle ReferenceHandler.Preserve

            if (!raw.length) {
                setLoadError(`No farm found for ID ${_plantID}.`);
                return;
            }

            // FIX: The SP may return all records (when @search_name = "SearchAllPlant").
            // Always find the record that matches the requested plant_id instead of
            // blindly taking raw[0] — that caused every edit to load the first DB row.
            const p = raw.find(r => String(r.plant_id) === String(_plantID)) || null;

            if (!p) {
                setLoadError(`Farm ID ${_plantID} was not found in the returned data. Check the stored procedure filters by @param_id.`);
                return;
            }

            // Convert stored JSON boundary back to editable CSV for the textarea
            const csvBoundary = convertJsonToBoundaryCSV(p.plant_boundry);

            setFormData({
                company_id: p.company_id ?? '',
                plant_name: p.plant_name ?? '',
                contact_person_name: p.contact_person_name ?? '',
                contact_number: p.contact_number ?? '',
                contact_email_id: p.contact_email_id ?? '',
                plant_address: p.plant_address ?? '',
                plant_lat: p.plant_lat ?? '',
                plant_lon: p.plant_lon ?? '',
                plant_area: p.plant_area ?? '',
                plant_boundry: csvBoundary,
                plant_time_zone_diff: p.plant_time_zone_diff ?? '',
                critical_battery_level: p.critical_battery_level ?? '',
                plant_added_date: p.plant_added_date ?? '',
                company_name: p.company_name ?? '',
                plant_id: p.plant_id ?? _plantID,
                plant_mode: 'Update',
                message: '',
            });

            // Show preview for the loaded boundary
            if (csvBoundary) {
                const { json, error } = convertBoundaryToJson(csvBoundary);
                setBoundaryPreview(error ? `⚠ ${error}` : json);
            }
        } catch (err) {
            console.error("EditPlant fetch error:", err);
            setLoadError("Failed to load farm details. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (name === "plant_boundry") {
            const { json, error } = convertBoundaryToJson(value);
            setBoundaryPreview(error ? `⚠ ${error}` : json);
        }
    };

    const validateForm = (data) => {
        const errs = {};

        if (!data.plant_name.trim())
            errs.plant_name = 'Farm name is required';
        else if (data.plant_name.length > 50)
            errs.plant_name = 'Farm name must not exceed 50 characters';

        if (!data.contact_person_name.trim())
            errs.contact_person_name = 'Contact person is required';
        else if (data.contact_person_name.length > 50)
            errs.contact_person_name = 'Contact person name must not exceed 50 characters';

        if (!data.contact_number.trim())
            errs.contact_number = 'Contact number is required';
        else if (data.contact_number.length > 35)
            errs.contact_number = 'Contact number must not exceed 35 characters';

        if (!data.contact_email_id.trim())
            errs.contact_email_id = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(data.contact_email_id))
            errs.contact_email_id = 'Email is invalid';

        if (!data.plant_address.trim())
            errs.plant_address = 'Farm address is required';
        else if (data.plant_address.length > 100)
            errs.plant_address = 'Farm address must not exceed 100 characters';

        if (!data.plant_lat.toString().trim())
            errs.plant_lat = 'Farm latitude is required';
        else if (isNaN(parseFloat(data.plant_lat)))
            errs.plant_lat = 'Farm latitude must be a valid number';

        if (!data.plant_lon.toString().trim())
            errs.plant_lon = 'Farm longitude is required';
        else if (isNaN(parseFloat(data.plant_lon)))
            errs.plant_lon = 'Farm longitude must be a valid number';

        if (!String(data.plant_time_zone_diff).trim())
            errs.plant_time_zone_diff = 'Time zone difference is required';

        if (!String(data.critical_battery_level).trim())
            errs.critical_battery_level = 'Battery level is required';

        if (!data.plant_boundry.trim()) {
            errs.plant_boundry = 'Farm boundary is required';
        } else {
            const { error } = convertBoundaryToJson(data.plant_boundry);
            if (error) errs.plant_boundry = error;
        }

        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm(formData);
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setFormData((prev) => ({ ...prev, message: 'Please fix the errors below.' }));
            return;
        }

        try {
            const { json: boundaryJson } = convertBoundaryToJson(formData.plant_boundry);

            const payload = {
                ...formData,
                plant_boundry: boundaryJson,
                user_id: _userID,
                plant_mode: 'Update',
            };

            await AxiosInstance.post("/Plant", payload);
            setFormData({ ...emptyForm, message: 'Farm updated successfully!' });
            //setFormData((prev) => ({ ...prev, message: 'Farm updated successfully!' }));
        } catch (err) {
            console.error("EditPlant submit error:", err);
            setFormData((prev) => ({ ...prev, message: 'Update failed. Please try again.' }));
        }
    };

    // -----------------------------------------------------------------------
    // Render states
    // -----------------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="container-ul" style={{ marginTop: '7%' }}>
                <div className="form-container">
                    <p><FaIcons.FaSpinner /> Loading farm details...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="container-ul" style={{ marginTop: '7%' }}>
                <div className="form-container">
                    <div className="alert alert-danger">{loadError}</div>
                    <a className="btndelete" href="#/search-plant">← Back to Search Farm</a>
                </div>
            </div>
        );
    }

    return (
        <div className="container-ul" style={{ marginTop: '13%' }}>
            <div className="form-container">
                <h2 className="form-title">
                    <FaIcons.FaRegEdit /> Edit Farm — ID: {formData.plant_id}
                </h2>

                {formData.message && (
                    <div className={`alert ${formData.message.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
                        {formData.message}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Company ID — read-only, cannot change parent company on edit */}
                    <div>
                        <input className="form-input" type="text"
                            value={`Company ID: ${formData.company_id}  |  ${formData.company_name}`}
                            readOnly
                            style={{ background: '#f5f5f5', color: '#555', cursor: 'default' }}
                        />
                    </div>
                    <div>
                        <input className="form-input" type="text" name="plant_name"
                            maxLength="50" value={formData.plant_name} onChange={handleChange}
                            placeholder="Farm name (max 50 chars)" />
                        {errors.plant_name && <span className="error-message">{errors.plant_name}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="contact_person_name"
                            maxLength="50" value={formData.contact_person_name} onChange={handleChange}
                            placeholder="Contact person (max 50 chars)" />
                        {errors.contact_person_name && <span className="error-message">{errors.contact_person_name}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="contact_number"
                            maxLength="35" value={formData.contact_number} onChange={handleChange}
                            placeholder="Contact number (max 35 chars)" />
                        {errors.contact_number && <span className="error-message">{errors.contact_number}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="contact_email_id"
                            maxLength="30" value={formData.contact_email_id} onChange={handleChange}
                            placeholder="Contact email (max 30 chars)" />
                        {errors.contact_email_id && <span className="error-message">{errors.contact_email_id}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="plant_address"
                            maxLength="100" value={formData.plant_address} onChange={handleChange}
                            placeholder="Farm address (max 100 chars)" />
                        {errors.plant_address && <span className="error-message">{errors.plant_address}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="plant_lat"
                            maxLength="15" value={formData.plant_lat} onChange={handleChange}
                            placeholder="Farm latitude  e.g. 11.234567" />
                        {errors.plant_lat && <span className="error-message">{errors.plant_lat}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="plant_lon"
                            maxLength="15" value={formData.plant_lon} onChange={handleChange}
                            placeholder="Farm longitude  e.g. 73.345678" />
                        {errors.plant_lon && <span className="error-message">{errors.plant_lon}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="plant_area"
                            maxLength="5" value={formData.plant_area} onChange={handleChange}
                            placeholder="Farm area (max 5 chars)" />
                        {errors.plant_area && <span className="error-message">{errors.plant_area}</span>}
                    </div>

                    {/* ---- Boundary textarea with live JSON preview ---- */}
                    <div>
                        <textarea
                            className="form-input"
                            name="plant_boundry"
                            value={formData.plant_boundry}
                            onChange={handleChange}
                            rows={3}
                            placeholder="lat, lon, lat, lon, ...  (min 3, max 15 pairs)"
                            style={{ resize: 'vertical', fontFamily: 'monospace' }}
                        />
                        {errors.plant_boundry && <span className="error-message">{errors.plant_boundry}</span>}
                        {formData.plant_boundry.trim() && (
                            <div style={{
                                marginTop: '4px', padding: '8px', borderRadius: '4px',
                                fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all',
                                background: boundaryPreview.startsWith('⚠') ? '#fff3cd' : '#d4edda',
                                color: boundaryPreview.startsWith('⚠') ? '#856404' : '#155724',
                            }}>
                                <strong>JSON preview: </strong>{boundaryPreview}
                            </div>
                        )}
                    </div>

                    <div>
                        <input className="form-input" type="text" name="plant_time_zone_diff"
                            maxLength="5" value={formData.plant_time_zone_diff} onChange={handleChange}
                            placeholder="Time zone difference (max 5 digits)" />
                        {errors.plant_time_zone_diff && <span className="error-message">{errors.plant_time_zone_diff}</span>}
                    </div>
                    <div>
                        <input className="form-input" type="text" name="critical_battery_level"
                            maxLength="3" value={formData.critical_battery_level} onChange={handleChange}
                            placeholder="Critical battery level (max 3 digits)" />
                        {errors.critical_battery_level && <span className="error-message">{errors.critical_battery_level}</span>}
                    </div>

                    <div className="row" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div className="col-5">
                            <button className="submit-button" type="submit">
                                <FaIcons.FaSave /> Update Farm
                            </button>
                        </div>
                        <div className="col-5">
                            <a style={{ textDecoration: 'none' }}
                                className="submit-button"
                                href="#/search-plant">
                                <FaIcons.FaSearch />&nbsp;Search Farm
                            </a>
                        </div>
                        {/*<div className="col-4">*/}
                        {/*    <a style={{ textDecoration: 'none' }}*/}
                        {/*        className="submit-button"*/}
                        {/*        href="#/add-plant">*/}
                        {/*        <FaIcons.FaSave />&nbsp;Add Farm*/}
                        {/*    </a>*/}
                        {/*</div>*/}
                    </div>
                </form>
                <p></p>
                <p></p>
            </div>
        </div>
    );
};

export default EditPlant;